import React, {useEffect, useRef, useState} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {cn} from '@rocket-ui/tools';
import {ROUTES} from 'app/router/routes';
import {UnreadNotificationsBadge, useUnreadNotificationsLabel} from 'features/notifications';
import {NotificationsIcon, OffersNavIcon, OrdersNavIcon, ProfileNavIcon} from 'shared/icons';
import {scrollAuthorizedMainToTop} from 'shared/lib/scrollAuthorizedMainToTop';
import {AuthorizedLayoutLogoutButton} from './AuthorizedLayoutLogoutButton';

const mainLinks = [
	{to: ROUTES.orders, label: 'Мои заказы', icon: OrdersNavIcon, scrollToTop: true},
	{to: ROUTES.offers, label: 'Предложения', icon: OffersNavIcon, scrollToTop: false},
] as const;

interface AuthorizedLayoutNavigationProps {
	variant: 'sidebar' | 'sheet';
	onLogout: () => void;
	onNavigate?: () => void;
}

function linkClassName(isActive: boolean, variant: 'sidebar' | 'sheet'): string {
	return cn(
		'flex h-14 items-center gap-3 text-base font-medium text-black transition-colors',
		variant === 'sidebar' ? 'mx-5 w-[calc(100%-2.5rem)] px-5' : 'w-full pl-10 pr-6',
		isActive ? 'bg-[#F0F0F0]' : 'hover:bg-[#F0F0F0]',
	);
}

export function AuthorizedLayoutNavigation({
	variant,
	onLogout,
	onNavigate,
}: AuthorizedLayoutNavigationProps): React.ReactElement {
	const {pathname} = useLocation();
	const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
	const accountMenuRef = useRef<HTMLDivElement>(null);
	const isProfile = pathname === ROUTES.profile || pathname.startsWith(`${ROUTES.profile}/`);
	const notificationsLabel = useUnreadNotificationsLabel();

	const handleNavigate = (scrollToTop: boolean) => () => {
		if (scrollToTop) {
			scrollAuthorizedMainToTop();
		}

		onNavigate?.();
	};

	useEffect(() => {
		setIsAccountMenuOpen(false);
	}, [pathname]);

	useEffect(() => {
		if (!isAccountMenuOpen) return;

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target;

			if (!(target instanceof Node)) return;
			if (accountMenuRef.current?.contains(target)) return;

			setIsAccountMenuOpen(false);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsAccountMenuOpen(false);
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isAccountMenuOpen]);

	const links = mainLinks.map((link) => {
		const Icon = link.icon;

		return (
			<NavLink
				key={link.to}
				to={link.to}
				onClick={handleNavigate(link.scrollToTop)}
				className={({isActive}) => linkClassName(isActive || (link.to === ROUTES.offers && pathname === '/'), variant)}
			>
				<span className="inline-flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
					<Icon />
				</span>
				<span>{link.label}</span>
			</NavLink>
		);
	});

	const notificationsLink = (
		<NavLink
			to={ROUTES.notifications}
			onClick={handleNavigate(true)}
			aria-label={notificationsLabel}
			className={({isActive}) => linkClassName(isActive, variant)}
		>
			<span className="relative inline-flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
				<NotificationsIcon />
				<UnreadNotificationsBadge />
			</span>
			<span>Уведомления</span>
		</NavLink>
	);

	if (variant === 'sheet') {
		return (
			<nav className="flex min-h-full flex-1 flex-col pt-3">
				{links}
				<div className="mt-auto">
					<NavLink
						to={ROUTES.profile}
						onClick={handleNavigate(true)}
						className={({isActive}) => linkClassName(isActive, 'sheet')}
					>
						<span className="inline-flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
							<ProfileNavIcon />
						</span>
						<span>Профиль</span>
					</NavLink>
					<div className="mx-4 my-2 h-px bg-black/10" />
					<AuthorizedLayoutLogoutButton onLogout={onLogout} className="h-14 pl-10 pr-6 hover:bg-[#F0F0F0]" />
				</div>
			</nav>
		);
	}

	return (
		<nav className="flex min-h-0 flex-1 flex-col pt-3">
			{links}
			<div className="mt-auto">
				<div className="mb-3">{notificationsLink}</div>
				<div ref={accountMenuRef} className="relative border-t border-black/10">
					{isAccountMenuOpen ? (
						<div
							role="menu"
							className="absolute bottom-full left-[72px] right-0 z-20 mb-2 bg-white shadow-[0_8px_28px_rgba(0,0,0,0.12)]"
						>
							<NavLink
								to={ROUTES.profile}
								role="menuitem"
								onClick={() => {
									scrollAuthorizedMainToTop();
									setIsAccountMenuOpen(false);
								}}
								className={({isActive}) =>
									cn(
										'flex h-14 items-center gap-3 px-4 text-base font-medium text-black transition-colors hover:bg-black/[0.04]',
										isActive && 'bg-[#F0F0F0]',
									)
								}
							>
								<span className="inline-flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
									<ProfileNavIcon />
								</span>
								<span>Профиль</span>
							</NavLink>
							<div className="h-px bg-black/10" />
							<AuthorizedLayoutLogoutButton onLogout={onLogout} className="h-14 px-4 hover:bg-black/[0.04]" />
						</div>
					) : null}
					<div className="pb-5 pt-3">
						<button
							type="button"
							className={cn(linkClassName(isAccountMenuOpen || isProfile, 'sidebar'), 'text-left')}
							aria-haspopup="menu"
							aria-expanded={isAccountMenuOpen}
							onClick={() => setIsAccountMenuOpen((value) => !value)}
						>
							<span className="inline-flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
								<ProfileNavIcon />
							</span>
							<span>Личный кабинет</span>
						</button>
					</div>
				</div>
			</div>
		</nav>
	);
}
